import React, { useState, useEffect } from 'react';
import { ColorPicker, Button, Select, Flex,  } from 'antd';
import Icon from 'components/AppIcon';
import { notify } from 'shared/utils/notify';
import { useUIPreferences } from 'shared/context/UIPreferencesContext';

const PREFERENCES_STORAGE_KEY = 'user_preferences';
const DEFAULT_ACCENT_COLOR = '#4F46E5';

const DEFAULT_PREFERENCES = {
  language: 'ru',
  timezone: 'Europe/Moscow',
  dateFormat: 'DD.MM.YYYY',
  timeFormat: '24h',
  sessionTimeout: '30',
  emailNotifications: {
    securityAlerts: true,
    systemUpdates: true,
    accountActivity: true,
    newsletter: false
  },
  uiPreferences: {
    theme: 'light',
    sidebarCollapsed: false,
    compactMode: false,
    showTooltips: true,
    primaryColor: null
  }
};

const Preferences = ({ onUnsavedChanges, hasUnsavedChanges }) => {
  const { prefs: uiPrefs, update: updateUiPrefs } = useUIPreferences();
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [originalPreferences, setOriginalPreferences] = useState(DEFAULT_PREFERENCES);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState('');

  // Load preferences from localStorage on mount; синхронизируем uiPreferences с UIPreferencesContext.
  useEffect(() => {
    try {
      const stored = localStorage.getItem(PREFERENCES_STORAGE_KEY);
      const base = stored ? { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) } : { ...DEFAULT_PREFERENCES };
      const merged = {
        ...base,
        uiPreferences: {
          theme: uiPrefs.theme,
          sidebarCollapsed: uiPrefs.sidebarCollapsed,
          compactMode: uiPrefs.compactMode,
          showTooltips: uiPrefs.showTooltips,
          primaryColor: uiPrefs.primaryColor,
        },
      };
      setPreferences(merged);
      setOriginalPreferences(merged);
    } catch (err) {
      console.error('Failed to load preferences:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePreferenceChange = (category, key, value) => {
    let newPreferences;
    
    if (category) {
      newPreferences = {
        ...preferences,
        [category]: {
          ...preferences[category],
          [key]: value
        }
      };
    } else {
      newPreferences = {
        ...preferences,
        [key]: value
      };
    }
    
    setPreferences(newPreferences);
    
    const hasChanges = JSON.stringify(newPreferences) !== JSON.stringify(originalPreferences);
    onUnsavedChanges(hasChanges);
  };

  const handleSave = () => {
    setIsSaving(true);
    try {
      // 1. UI-настройки — реально применяются через UIPreferencesContext.
      updateUiPrefs({
        theme: preferences.uiPreferences.theme,
        sidebarCollapsed: preferences.uiPreferences.sidebarCollapsed,
        compactMode: preferences.uiPreferences.compactMode,
        showTooltips: preferences.uiPreferences.showTooltips,
        primaryColor: preferences.uiPreferences.primaryColor,
      });

      // 2. Прочие настройки пока сохраняем в localStorage без бэкенд-синхронизации.
      localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
      setOriginalPreferences({ ...preferences });
      onUnsavedChanges(false);

      // 3. Определяем, изменились ли «в разработке»-секции — если да,
      //    показываем info-тост, что эти изменения в бэкенд не отправлены.
      const backendKeys = ['language', 'timezone', 'dateFormat', 'timeFormat', 'sessionTimeout'];
      const backendChanged = backendKeys.some(
        (k) => preferences[k] !== originalPreferences[k]
      );
      const emailNotifChanged = JSON.stringify(preferences.emailNotifications) !==
        JSON.stringify(originalPreferences.emailNotifications);

      notify.success('Настройки интерфейса сохранены');
      if (backendChanged || emailNotifChanged) {
        notify.wip('Язык / часовой пояс / email-уведомления');
      }

      setSaveSuccess('Настройки успешно сохранены');
      setTimeout(() => setSaveSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to save preferences:', err);
      notify.error('Не удалось сохранить настройки');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setPreferences({ ...originalPreferences });
    onUnsavedChanges(false);
  };

  const languages = [
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' }
  ];

  const timezones = [
    { value: 'Europe/Moscow', label: 'Москва (UTC+3)' },
    { value: 'Europe/London', label: 'Лондон (UTC+0)' },
    { value: 'America/New_York', label: 'Нью-Йорк (UTC-5)' },
    { value: 'Asia/Tokyo', label: 'Токио (UTC+9)' }
  ];

  const sessionTimeouts = [
    { value: '15', label: '15 минут' },
    { value: '30', label: '30 минут' },
    { value: '60', label: '1 час' },
    { value: '120', label: '2 часа' },
    { value: '480', label: '8 часов' },
    { value: '0', label: 'Никогда' }
  ];

  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-text-primary">
          Настройки и предпочтения
        </h2>
        <p className="text-text-secondary mt-1">
          Настройте язык, часовой пояс, уведомления и другие предпочтения
        </p>
      </div>

      {/* Success Message */}
      {saveSuccess && (
        <div className="p-4 bg-success/10 border border-success/20 rounded-md flex items-center gap-2">
          <Icon name="CheckCircle" size={18} className="text-success" />
          <p className="text-sm text-success">{saveSuccess}</p>
        </div>
      )}

      {/* Language and Region */}
      <div className="bg-secondary-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-text-primary mb-4 flex items-center gap-2">
          <Icon name="Globe" size={20} className="text-primary" />
          Язык и регион
          <WipBadge />
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Язык интерфейса
            </label>
            <Select
              value={preferences.language}
              onChange={(e) => handlePreferenceChange(null, 'language', e.target.value)}
              className="
                w-full px-3 py-2 border border-secondary-300 rounded-md
                focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                transition-colors duration-150 bg-surface
              "
            >
              {languages.map(lang => (
                <Select.Option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </Select.Option>
              ))}
            </Select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Часовой пояс
            </label>
            <Select
              value={preferences.timezone}
              onChange={(e) => handlePreferenceChange(null, 'timezone', e.target.value)}
              className="
                w-full px-3 py-2 border border-secondary-300 rounded-md
                focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                transition-colors duration-150 bg-surface
              "
            >
              {timezones.map(tz => (
                <Select.Option key={tz.value} value={tz.value}>
                  {tz.label}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Формат даты
            </label>
            <Select
              value={preferences.dateFormat}
              onChange={(e) => handlePreferenceChange(null, 'dateFormat', e.target.value)}
              className="
                w-full px-3 py-2 border border-secondary-300 rounded-md
                focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                transition-colors duration-150 bg-surface
              "
            >
              <Select.Option value="DD.MM.YYYY">ДД.ММ.ГГГГ (15.12.2024)</Select.Option>
              <Select.Option value="MM/DD/YYYY">ММ/ДД/ГГГГ (12/15/2024)</Select.Option>
              <Select.Option value="YYYY-MM-DD">ГГГГ-ММ-ДД (2024-12-15)</Select.Option>
            </Select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Формат времени
            </label>
            <Select
              value={preferences.timeFormat}
              onChange={(e) => handlePreferenceChange(null, 'timeFormat', e.target.value)}
              className="
                w-full px-3 py-2 border border-secondary-300 rounded-md
                focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                transition-colors duration-150 bg-surface
              "
            >
              <Select.Option value="24h">24-часовой (14:30)</Select.Option>
              <Select.Option value="12h">12-часовой (2:30 PM)</Select.Option>
            </Select>
          </div>
        </div>
      </div>

      {/* Session Settings */}
      <div className="bg-secondary-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-text-primary mb-4 flex items-center gap-2">
          <Icon name="Clock" size={20} className="text-primary" />
          Настройки сессии
          <WipBadge />
        </h3>
        
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Автоматический выход из системы
          </label>
          <Select
            value={preferences.sessionTimeout}
            onChange={(e) => handlePreferenceChange(null, 'sessionTimeout', e.target.value)}
            className="
              w-full md:w-1/2 px-3 py-2 border border-secondary-300 rounded-md
              focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
              transition-colors duration-150 bg-surface
            "
          >
            {sessionTimeouts.map(timeout => (
              <Select.Option key={timeout.value} value={timeout.value}>
                {timeout.label}
              </Select.Option>
            ))}
          </Select>
          <p className="text-sm text-text-secondary mt-2">
            Система автоматически завершит сессию после указанного периода неактивности
          </p>
        </div>
      </div>

      {/* Email Notifications */}
      <div className="bg-secondary-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-text-primary mb-4 flex items-center gap-2">
          <Icon name="Mail" size={20} className="text-primary" />
          Email уведомления
          <WipBadge />
        </h3>
        
        <div className="space-y-4">
          {Object.entries(preferences.emailNotifications).map(([key, value]) => {
            const labels = {
              securityAlerts: 'Уведомления о безопасности',
              systemUpdates: 'Системные обновления',
              accountActivity: 'Активность аккаунта',
              newsletter: 'Новостная рассылка'
            };
            
            const descriptions = {
              securityAlerts: 'Получать уведомления о подозрительной активности и изменениях безопасности',
              systemUpdates: 'Уведомления о обновлениях системы и новых функциях',
              accountActivity: 'Уведомления о входах в систему и изменениях профиля',
              newsletter: 'Периодические новости и советы по использованию системы'
            };

            return (
              <div key={key} className="flex items-start justify-between">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-text-primary">
                    {labels[key]}
                  </label>
                  <p className="text-xs text-text-secondary mt-1">
                    {descriptions[key]}
                  </p>
                </div>
                <button
                  onClick={() => handlePreferenceChange('emailNotifications', key, !value)}
                  className={`
                    relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-150
                    focus:outline-none focus:ring-2 focus:ring-primary/20 ml-4
                    ${value ? 'bg-primary' : 'bg-secondary-300'}
                  `}
                >
                  <span
                    className={`
                      inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-150
                      ${value ? 'translate-x-6' : 'translate-x-1'}
                    `}
                  />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* UI Preferences */}
      <div className="bg-secondary-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-text-primary mb-4 flex items-center gap-2">
          <Icon name="Palette" size={20} className="text-primary" />
          Настройки интерфейса
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Тема оформления
            </label>
            <div className="flex gap-3 flex-wrap">
              {[
                { value: 'light', label: 'Светлая', icon: 'Sun' },
                { value: 'dark', label: 'Темная', icon: 'Moon' },
                { value: 'auto', label: 'Авто', icon: 'Monitor' }
              ].map(theme => (
                <Button
                  key={theme.value}
                  onClick={() => handlePreferenceChange('uiPreferences', 'theme', theme.value)}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-md border transition-colors duration-150
                    ${preferences.uiPreferences.theme === theme.value
                      ? 'border-primary bg-primary text-white' :'border-secondary-300 bg-surface text-text-primary hover:bg-secondary-50'
                    }
                  `}
                >
                  <Icon name={theme.icon} size={16} />
                  {theme.label}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Акцентный цвет
            </label>
            <div className="flex flex-wrap items-center gap-3">
              <ColorPicker
                showText
                value={preferences.uiPreferences.primaryColor || DEFAULT_ACCENT_COLOR}
                onChange={(color) => handlePreferenceChange('uiPreferences', 'primaryColor', color.toHexString())}
              />
              {preferences.uiPreferences.primaryColor && (
                <Button
                  type="button"
                  onClick={() => handlePreferenceChange('uiPreferences', 'primaryColor', null)}
                  className="text-sm text-text-secondary hover:text-primary transition-colors duration-150"
                >
                  Сбросить к цвету по умолчанию
                </Button>
              )}
            </div>
            <p className="text-xs text-text-secondary mt-2">
              Применяется как основной (primary) цвет кнопок, ссылок и выделений во всём интерфейсе
            </p>
          </div>

          {Object.entries(preferences.uiPreferences).filter(([key]) => key !== 'theme' && key !== 'primaryColor').map(([key, value]) => {
            const labels = {
              sidebarCollapsed: 'Свернутая боковая панель',
              compactMode: 'Компактный режим',
              showTooltips: 'Показывать подсказки'
            };
            
            const descriptions = {
              sidebarCollapsed: 'Боковая панель навигации будет свернута по умолчанию',
              compactMode: 'Уменьшенные отступы и более плотное расположение элементов',
              showTooltips: 'Показывать всплывающие подсказки при наведении на элементы'
            };

            return (
              <div key={key} className="flex items-start justify-between">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-text-primary">
                    {labels[key]}
                  </label>
                  <p className="text-xs text-text-secondary mt-1">
                    {descriptions[key]}
                  </p>
                </div>
                <button
                  onClick={() => handlePreferenceChange('uiPreferences', key, !value)}
                  className={`
                    relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-150
                    focus:outline-none focus:ring-2 focus:ring-primary/20 ml-4
                    ${value ? 'bg-primary' : 'bg-secondary-300'}
                  `}
                >
                  <span
                    className={`
                      inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-150
                      ${value ? 'translate-x-6' : 'translate-x-1'}
                    `}
                  />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Action Buttons */}
      {hasUnsavedChanges && (
        <div className="flex items-center justify-end gap-3 pt-6 border-t border-secondary-200">
          <Button
            onClick={handleReset}
            className="
              px-4 py-2 border border-secondary-300 text-text-primary rounded-md
              hover:bg-secondary-50 transition-colors duration-150 hover-lift
              focus:outline-none focus:ring-2 focus:ring-primary/20
            "
          >
            Сбросить
          </Button>
          <Button
            onClick={handleSave}
            className="
              px-4 py-2 bg-primary text-white rounded-md
              hover:bg-primary-700 transition-colors duration-150 hover-lift
              focus:outline-none focus:ring-2 focus:ring-primary/20
              flex items-center gap-2
            "
          >
            <Icon name="Save" size={16} />
            Сохранить настройки
          </Button>
        </div>
      )}
    </div>
  );
};

export default Preferences;

/**
 * Маленький badge «в разработке» для секций, которые пока не подкреплены
 * реальными бэкенд-эндпоинтами.
 */
function WipBadge() {
  return (
    <span
      className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-warning/10 text-warning border border-warning/20"
      title="Сохраняется локально, но ещё не применяется на сервере"
    >
      <Icon name="Wrench" size={12} />
      в разработке
    </span>
  );
}